import { useMemo } from 'react';
import type { ReactNode } from 'react';

// A small, dependency-free Markdown renderer for chat replies. It covers the subset models
// actually produce (headings, lists, quotes, code, emphasis, links) and builds React elements
// directly, so model output is never injected as HTML.

type AppMdBlock =
  | { type: 'paragraph'; lines: string[] }
  | { type: 'heading'; level: number; text: string }
  | { type: 'code'; code: string }
  | { type: 'quote'; blocks: AppMdBlock[] }
  | { type: 'list'; ordered: boolean; start: number; items: AppMdBlock[][] }
  | { type: 'rule' };

const FENCE_OPEN = /^\s*(`{3,}|~{3,})/;
const HEADING = /^\s{0,3}(#{1,6})\s+(.*?)(?:\s+#+)?\s*$/;
const RULE = /^\s{0,3}([-*_])(?:\s*\1){2,}\s*$/;
const QUOTE = /^\s{0,3}>\s?(.*)$/;
const LIST_ITEM = /^(\s*)([-*+]|\d{1,9}[.)])\s+(.*)$/;
const INLINE_SOURCE = [
  '`([^`\\n]+)`',
  '\\[([^\\]\\n]+)\\]\\(([^()\\s]+)\\)',
  '\\*\\*(.+?)\\*\\*',
  '~~(.+?)~~',
  '\\*([^*\\s](?:[^*\\n]*[^*\\s])?)\\*',
  '(https?:\\/\\/[^\\s<]*[^\\s<.,:;"\'!?)\\]])'
].join('|');
const SAFE_HREF = /^(https?:|mailto:)/i;

const leadingSpaces = (line: string): number => line.length - line.trimStart().length;

const startsBlock = (line: string): boolean => {
  return FENCE_OPEN.test(line) || HEADING.test(line) || RULE.test(line) || QUOTE.test(line) || LIST_ITEM.test(line);
};

const parseList = (lines: string[], start: number): { block: AppMdBlock; next: number } => {
  const first = lines[start].match(LIST_ITEM)!;
  const indent = first[1].length;
  const ordered = /\d/.test(first[2]);
  const items: string[][] = [];
  let contentColumn = 0;
  let blankPending = false;
  let i = start;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) {
      blankPending = true;
      i++;
      continue;
    }

    const item = line.match(LIST_ITEM);
    const lineIndent = leadingSpaces(line);

    if (item && lineIndent <= indent + 1) {
      // A sibling marker of the other list kind ends this list and starts a new one.
      if (/\d/.test(item[2]) !== ordered) {
        break;
      }

      items.push([item[3]]);
      contentColumn = item[1].length + item[2].length + 1;
    } else if (lineIndent > indent + 1) {
      const current = items[items.length - 1];

      if (blankPending) {
        current.push('');
      }
      current.push(line.slice(Math.min(lineIndent, contentColumn)));
    } else if (!blankPending && !startsBlock(line)) {
      // Lazy continuation of the previous item's text.
      items[items.length - 1].push(line.trim());
    } else {
      break;
    }

    blankPending = false;
    i++;
  }

  return {
    block: {
      type: 'list',
      ordered,
      start: ordered ? parseInt(first[2], 10) : 1,
      items: items.map(parseBlocks)
    },
    next: i
  };
};

const parseBlocks = (lines: string[]): AppMdBlock[] => {
  const blocks: AppMdBlock[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) {
      i++;
      continue;
    }

    const fence = line.match(FENCE_OPEN);

    if (fence) {
      const marker = fence[1];
      const code: string[] = [];

      i++;
      // An unclosed fence runs to the end of the message.
      while (i < lines.length && lines[i].trim() !== marker) {
        code.push(lines[i]);
        i++;
      }
      i++;
      blocks.push({ type: 'code', code: code.join('\n') });
      continue;
    }

    const heading = line.match(HEADING);

    if (heading) {
      blocks.push({ type: 'heading', level: heading[1].length, text: heading[2] });
      i++;
      continue;
    }

    if (RULE.test(line)) {
      blocks.push({ type: 'rule' });
      i++;
      continue;
    }

    if (QUOTE.test(line)) {
      const quoted: string[] = [];

      while (i < lines.length && QUOTE.test(lines[i])) {
        quoted.push(lines[i].match(QUOTE)![1]);
        i++;
      }
      blocks.push({ type: 'quote', blocks: parseBlocks(quoted) });
      continue;
    }

    if (LIST_ITEM.test(line)) {
      const { block, next } = parseList(lines, i);

      blocks.push(block);
      i = next;
      continue;
    }

    const paragraph: string[] = [];

    while (i < lines.length && lines[i].trim() && (paragraph.length === 0 || !startsBlock(lines[i]))) {
      paragraph.push(lines[i].trim());
      i++;
    }
    blocks.push({ type: 'paragraph', lines: paragraph });
  }

  return blocks;
};

const renderInline = (text: string, keyPrefix: string): ReactNode[] => {
  // A fresh regex per call keeps recursive calls from sharing lastIndex.
  const regex = new RegExp(INLINE_SOURCE, 'g');
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    const key = `${keyPrefix}-${match.index}`;

    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));

    if (match[1] !== undefined) {
      parts.push(<code key={key} className="md-code">{match[1]}</code>);
    } else if (match[2] !== undefined) {
      parts.push(SAFE_HREF.test(match[3])
        ? <a key={key} href={match[3]} target="_blank" rel="noopener noreferrer">{renderInline(match[2], key)}</a>
        : <span key={key}>{renderInline(match[2], key)}</span>);
    } else if (match[4] !== undefined) {
      parts.push(<strong key={key}>{renderInline(match[4], key)}</strong>);
    } else if (match[5] !== undefined) {
      parts.push(<del key={key}>{renderInline(match[5], key)}</del>);
    } else if (match[6] !== undefined) {
      parts.push(<em key={key}>{renderInline(match[6], key)}</em>);
    } else if (match[7] !== undefined) {
      parts.push(<a key={key} href={match[7]} target="_blank" rel="noopener noreferrer">{match[7]}</a>);
    }
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) parts.push(text.slice(lastIndex));

  return parts;
};

const HEADING_TAGS = ['h3', 'h4', 'h5', 'h6', 'h6', 'h6'] as const;

const renderBlocks = (blocks: AppMdBlock[], keyPrefix = 'md'): ReactNode[] => blocks.map((block, index) => {
  const key = `${keyPrefix}-${index}`;

  switch (block.type) {
    case 'paragraph':
      return (
        <p key={key}>
          {block.lines.map((line, lineIndex) => (
            <span key={lineIndex}>
              {lineIndex > 0 && <br />}
              {renderInline(line, `${key}-${lineIndex}`)}
            </span>
          ))}
        </p>
      );
    case 'heading': {
      const Tag = HEADING_TAGS[block.level - 1];

      return <Tag key={key}>{renderInline(block.text, key)}</Tag>;
    }
    case 'code':
      return <pre key={key}><code>{block.code}</code></pre>;
    case 'quote':
      return <blockquote key={key}>{renderBlocks(block.blocks, key)}</blockquote>;
    case 'list': {
      const items = block.items.map((item, itemIndex) => (
        <li key={itemIndex}>{renderBlocks(item, `${key}-${itemIndex}`)}</li>
      ));

      return block.ordered
        ? <ol key={key} start={block.start}>{items}</ol>
        : <ul key={key}>{items}</ul>;
    }
    case 'rule':
      return <hr key={key} />;
  }
});

export function AppMarkdown({ text }: { text: string }) {
  const blocks = useMemo(() => parseBlocks(text.replace(/\r\n?/g, '\n').split('\n')), [text]);

  return <div className="app-md">{renderBlocks(blocks)}</div>;
}
